/* oxlint-disable tailwindcss/no-unknown-classes -- Styles are colocated in home-new.css. */
import { Button } from "@ui/components/primitives/Button"
import { useEffect, useRef } from "react"

import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"

import { SiteSection } from "../site/SiteSection"
import { HomeNewPanelBackdrop } from "./HomeNewPanelBackdrop"
import {
  homeNewProductIds,
  homeNewProducts,
  type HomeNewProduct,
  type HomeNewProductClaim,
} from "./homeNewProducts"
import { SectionInViewProvider, useTrackInView } from "./sectionInView"
import { useActiveSection } from "./useActiveSection"
import { useScrollToProduct } from "./useScrollToProduct"

function ProductNav({ activeId }: { activeId: string | null }) {
  const scrollToProduct = useScrollToProduct()
  const navRef = useRef<HTMLElement>(null)

  const prefersReducedMotion = usePrefersReducedMotion()
  // On narrow screens the menu is a horizontal strip that scrolls sideways;
  // keep the active entry centred in it as the reader moves through the
  // sections. On desktop the menu is a column that doesn't scroll, so this
  // does nothing there.
  useEffect(() => {
    const strip = navRef.current?.parentElement
    const link = navRef.current?.querySelector<HTMLElement>(`[data-product="${activeId}"]`)
    if (!strip || !link || strip.scrollWidth <= strip.clientWidth) return
    const left = link.offsetLeft - (strip.clientWidth - link.offsetWidth) / 2
    strip.scrollTo({
      left: Math.max(0, left),
      behavior: prefersReducedMotion ? "auto" : "smooth",
    })
  }, [activeId, prefersReducedMotion])

  return (
    <nav ref={navRef} aria-label="Products" className="flex flex-col gap-0.5">
      {homeNewProducts.map(({ id, Icon, nav }) => {
        const active = id === activeId
        return (
          <a
            key={id}
            href={`#${id}`}
            data-product={id}
            aria-current={active ? "location" : undefined}
            onClick={(event) => scrollToProduct(event, id)}
            data-active={active ? "true" : "false"}
            className={`home-new-nav-link flex shrink-0 items-start gap-3 rounded-lg p-3 font-sans text-sm leading-5 font-medium focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus lg:w-full lg:px-4 ${
              active ? "" : "hover:bg-black/2 dark:hover:bg-white/3"
            }`}
          >
            {/* 16px icon with a fixed 1.25px stroke, centred on the title's line. */}
            <span className="flex h-5 shrink-0 items-center">
              <Icon
                size={16}
                strokeWidth={1.25}
                absoluteStrokeWidth
                className="text-default"
                aria-hidden="true"
              />
            </span>
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="whitespace-nowrap text-default">{nav.label}</span>
              <span className="hidden text-muted lg:block">{nav.description}</span>
            </span>
          </a>
        )
      })}
    </nav>
  )
}

// Claims stack below md and sit two per row above it, so each one after the
// first gets a top rule when stacked, and a left rule (right column) or top
// rule (later rows) in the grid.
function ProductClaim({ claim, index }: { claim: HomeNewProductClaim; index: number }) {
  const border =
    index === 0
      ? ""
      : `border-t border-subtle ${index % 2 === 1 ? "md:border-l" : ""} ${index < 2 ? "md:border-t-0" : ""}`
  return (
    <div className={`flex min-h-[400px] min-w-0 flex-col overflow-clip p-6 ${border}`}>
      <h3 className="font-headline text-base leading-5 font-medium tracking-[0.01em] text-default">
        {claim.title}
      </h3>
      <p className="mt-2 min-h-10 font-sans text-sm leading-5 font-medium text-muted">
        {claim.description}
      </p>
      <div className="mt-8 flex min-h-[244px] flex-1 flex-col justify-center">{claim.visual}</div>
    </div>
  )
}

function ProductSection({ product }: { product: HomeNewProduct }) {
  const { id, intro, cta, claims, showcase, backdrop } = product
  const sectionRef = useRef<HTMLElement>(null)
  const inView = useTrackInView(sectionRef)

  return (
    <SectionInViewProvider value={inView}>
      <section
        ref={sectionRef}
        // Offscreen sections pause their CSS loops (home-new.css) and scenes
        // read `inView` to stop their timers.
        data-offscreen={inView ? undefined : "true"}
        id={id}
        data-product={id}
        aria-labelledby={`${id}-title`}
        // Below lg the product bar pins across the top (61px: one row of menu
        // entries), so a link straight to #section, e.g. a shared URL, stops
        // clear of it. Clicks in the page measure the bar (useScrollToProduct).
        className="border-subtle not-first:border-t max-lg:scroll-mt-[61px]"
      >
        <div className="flex flex-col gap-6 px-6 py-10 sm:flex-row sm:items-start sm:justify-between sm:gap-8 sm:px-10 lg:p-16">
          <h2
            id={`${id}-title`}
            className="max-w-[494px] font-headline text-2xl leading-7 font-medium text-default"
          >
            {intro.lead}
            {intro.runOn ? null : <br className="hidden sm:block" />}{" "}
            <span className="text-muted">{intro.rest}</span>
          </h2>
          {cta ? (
            <Button
              to={cta.href}
              variant="outline"
              className="shrink-0 self-start whitespace-nowrap"
              aria-label={`Learn more about ${product.nav.label}`}
            >
              Learn more
            </Button>
          ) : null}
        </div>

        {/* Sections without art go straight from the intro to the claims. */}
        {backdrop ? (
          <div
            className="relative isolate h-[360px] overflow-hidden border-t border-subtle sm:h-[560px]"
            aria-hidden="true"
          >
            <HomeNewPanelBackdrop {...backdrop} />
            {showcase}
          </div>
        ) : null}

        {/* Without a panel the claims sit on the darker sunken grey, so they read
          as their own band under the intro. */}
        <div
          className={`grid border-t border-subtle md:grid-cols-2 ${backdrop ? "" : "bg-sunken"}`}
        >
          {claims.map((claim, index) => (
            <ProductClaim key={claim.title + index} claim={claim} index={index} />
          ))}
        </div>
      </section>
    </SectionInViewProvider>
  )
}

export function HomeNewFeaturesSection() {
  const activeId = useActiveSection(homeNewProductIds)

  return (
    <SiteSection innerClassName="grid lg:grid-cols-3">
      {/* The menu pins to the top of the viewport and follows the reader
          through the product sections; the active entry tracks the section
          currently in view. On a viewport shorter than the menu it scrolls
          inside itself, so the lower entries stay reachable. Below lg it
          collapses to a horizontal strip. */}
      <div
        data-product-bar
        className="sticky top-0 z-10 min-w-0 border-b border-subtle bg-base lg:max-h-screen lg:self-start lg:overflow-y-auto lg:border-b-0 lg:py-16 lg:pr-10 lg:pl-16"
      >
        <div className="hidden flex-col gap-3 pl-3 lg:flex">
          <p className="font-sans text-sm leading-5 font-medium text-default">Join the rebels</p>
          <h2 className="font-headline text-[32px] leading-none font-medium text-default">
            Build on Entire.
          </h2>
        </div>
        {/* The strip's side padding sits on the nav, inside the scrollable content, so
            the last entry keeps its gutter at the end of the scroll (WebKit drops a
            scroll container's own end padding). */}
        <div className="scrollbar-none overflow-x-auto py-2 lg:-mx-1 lg:mt-10 lg:overflow-visible lg:p-0 [&>nav]:flex-row max-lg:[&>nav]:w-max max-lg:[&>nav]:px-3 lg:[&>nav]:flex-col">
          <ProductNav activeId={activeId} />
        </div>
      </div>

      <div className="min-w-0 lg:col-span-2 lg:border-l lg:border-subtle">
        {homeNewProducts.map((product) => (
          <ProductSection key={product.id} product={product} />
        ))}
      </div>
    </SiteSection>
  )
}
